import { useRef, useState, useEffect } from "react";
import { UserCircleIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import UploadIcon from "../assets/icons/UploadIcon.svg";
import { profileApi } from "../../services/api";
const UserAvatar = ({ user, variant, rightSlot }) => {
    const fileInputRef = useRef(null);

    // Store the full image URL
    const [imageUrl, setImageUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);


    const fetchProfileImage = async () => {
        // Prevent multiple simultaneous fetches
        if (isFetching) {
            // console.log("Already fetching profile image, skipping...");
            return;
        }

        try {
            setIsFetching(true);
            let filename;

            // 1. Try to get filename from user context first (fastest)
            // Note: user.profile_image might be an object now {file_name, url, ...}
            if (user?.profile_image?.file_name) {
                filename = user.profile_image.file_name;
            } else {
                // 2. Fallback: fetch meta from API
                const metaRes = await profileApi.getProfileImageMeta();
                if (metaRes.data && metaRes.data.profileImageName) {
                    filename = metaRes.data.profileImageName;
                }
            }

            if (!filename) {
                // console.log("No profile image filename found");
                return;
            }

            // console.log("Fetching profile image for:", filename);
            const res = await profileApi.downloadImage(filename);

            if (res.data) {
                const blobUrl = URL.createObjectURL(res.data);
                setImageUrl(blobUrl);
            }
        } catch (err) {
            console.error("Fetch profile image failed:", err);

            // Try to read the blob error message
            if (err.response && err.response.data instanceof Blob) {
                const text = await err.response.data.text();
                console.error("Server Error details:", text);
            }
        } finally {
            setIsFetching(false);
        }
    };


    // Fetch existing profile image on mount or when profile image filename changes
    useEffect(() => {
        // console.log("DEBUG: UserAvatar useEffect triggered");
        // console.log("DEBUG: profile_image:", user?.profile_image);

        // Trigger fetch if user has profile_image data
        if (user?.profile_image) {
            // console.log("DEBUG: profile_image found, calling fetchProfileImage");
            fetchProfileImage();
        } else {
            // console.log("DEBUG: No profile_image in user object");
            // Clear image if no profile image
            if (imageUrl) {
                URL.revokeObjectURL(imageUrl);
                setImageUrl(null);
            }
        }
    }, [user?.profile_image?.file_name]); // Only re-run when the filename changes

    // Listen for profile image updates from other UserAvatar instances
    useEffect(() => {
        const handleProfileImageUpdate = async (e) => {
            const { fileName } = e.detail;
            if (!fileName) return;
            try {
                const res = await profileApi.downloadImage(fileName);
                if (res.data) {
                    if (imageUrl && imageUrl.startsWith('blob:')) {
                        URL.revokeObjectURL(imageUrl);
                    }
                    const blobUrl = URL.createObjectURL(res.data);
                    setImageUrl(blobUrl);
                }
            } catch (err) {
                console.error("Failed to refresh avatar after update:", err);
            }
        };

        window.addEventListener('profile-image-updated', handleProfileImageUpdate);
        return () => {
            window.removeEventListener('profile-image-updated', handleProfileImageUpdate);
        };
    }, []);

    // Cleanup blob URLs on unmount
    useEffect(() => {
        return () => {
            if (imageUrl && imageUrl.startsWith('blob:')) {
                URL.revokeObjectURL(imageUrl);
            }
        };
    }, [imageUrl]);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setLoading(true);

            const formData = new FormData();
            formData.append("file", file);

            // Step 1: Upload File
            const uploadRes = await profileApi.uploadImage(formData);
            const fileName = uploadRes.data.fileName;

            if (!fileName) throw new Error("Upload failed, no filename returned");

            // Step 2: Update Profile with Filename
            await profileApi.updateProfileImage({
                profileImageName: fileName
            });

            toast.success("Profile updated");

            // Step 3: Refresh Display
            // We can directly use the uploaded file to preview immediately
            const objectUrl = URL.createObjectURL(file);
            setImageUrl(objectUrl);

            // Step 4: Persist to localStorage without triggering auth context re-render
            // This avoids re-rendering all useAuth() consumers (which causes page refresh)
            try {
                const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
                storedUser.profile_image = { file_name: fileName };
                localStorage.setItem('user', JSON.stringify(storedUser));
            } catch (e) {
                console.error("Failed to update localStorage:", e);
            }

            // Step 5: Notify other UserAvatar instances (e.g. navbar) to update
            window.dispatchEvent(new CustomEvent('profile-image-updated', {
                detail: { fileName }
            }));

        } catch (err) {
            console.error("Upload failed", err);
            toast.error("Failed to upload avatar");
        } finally {
            setLoading(false);
        }
    };

    // If variant is 'circle', render a large circular avatar with an edit badge (click to upload)
    if (variant === 'circle') {
        return (
            <>
                <div
                    className="relative h-24 w-24 cursor-pointer group"
                    onClick={() => fileInputRef.current.click()}
                >
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt="Profile"
                            className="h-24 w-24 rounded-full object-cover ring-4 ring-white shadow-lg"
                        />
                    ) : (
                        <div
                            className="h-24 w-24 rounded-full ring-4 ring-white shadow-lg flex items-center justify-center"
                            style={{ background: "linear-gradient(120deg, #1d4ed8, #0a1f44)" }}
                        >
                            <UserCircleIcon className="h-16 w-16 text-white/90" />
                        </div>
                    )}

                    {/* Upload badge */}
                    <div className="absolute bottom-0.5 right-0.5 h-7 w-7 rounded-full bg-gradient-to-br from-[#163469] to-[#0a1f44] flex items-center justify-center ring-2 ring-white transition-transform group-hover:scale-110">
                        <img src={UploadIcon} alt="Upload" className="h-3.5 w-3.5" />
                    </div>

                    {loading && (
                        <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center text-[10px] text-white">
                            Uploading…
                        </div>
                    )}
                </div>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFileChange}
                />
            </>
        );
    }

    // If variant is 'small' (for navbar), just return the image/circle
    if (variant === 'small') {
        return (
            <div className="h-full w-full flex items-center justify-center">
                {imageUrl ? (
                    <img
                        src={imageUrl}
                        alt="Profile"
                        className="h-full w-full rounded-full object-cover"
                    />
                ) : (
                    <UserCircleIcon className="h-full w-full text-white" />
                )}
            </div>
        );
    }

    return (
        <div className="p-4 border-b border-gray-100">
            <div className="flex items-center gap-3 min-w-0">
                {/* Avatar */}
                <div
                    className="relative h-12 w-12 shrink-0 cursor-pointer"
                    onClick={() => fileInputRef.current.click()}
                >
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt="Profile"
                            className="h-12 w-12 rounded-full object-cover border"
                        />
                    ) : (
                        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#163469] to-[#0a1f44] p-[2px]">
                            <div className="h-full w-full rounded-full bg-white flex items-center justify-center">
                                <UserCircleIcon className="h-8 w-8 text-[#163469]" />
                            </div>
                        </div>
                    )}

                    {/* Camera badge */}
                    <div className="absolute -bottom-1 -right-1 bg-gradient-to-br from-[#163469] to-[#0a1f44] rounded-full p-1">
                        <img src={UploadIcon} alt="Upload" className="h-3 w-3" />
                    </div>

                    {loading && (
                        <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center text-xs text-white">
                            Uploading…
                        </div>
                    )}
                </div>

                <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 break-words">{user?.full_name}</p>
                    <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                </div>

                {rightSlot && <div className="shrink-0">{rightSlot}</div>}

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFileChange}
                />
            </div>
        </div>
    );
};

export default UserAvatar;
